import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { 
  Home, DollarSign, Handshake, ArrowRight, FileText, 
  Clock, ShieldCheck, Phone, CheckCircle2 
} from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';

const HowItWorksPage = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const steps = [
    {
      icon: Home,
      number: '01',
      title: 'Submit Your Property Info',
      description: 'Fill out a simple form with basic details about your home. It takes less than 5 minutes – no paperwork, no obligations.',
      details: [
        'Property address and basic details',
        'Your contact information',
        'Any specific circumstances or timeline',
      ],
      image: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
    },
    {
      icon: DollarSign,
      number: '02',
      title: 'Receive Your Cash Offer',
      description: 'Our team evaluates your property and presents you with a fair, no-obligation cash offer within 24 hours.',
      details: [
        'Fair market evaluation',
        'No hidden fees or deductions',
        'Transparent pricing breakdown',
      ],
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
    },
    {
      icon: Handshake,
      number: '03',
      title: 'Close on Your Terms',
      description: 'Accept the offer and choose your closing date. We handle all the paperwork and pay all closing costs.',
      details: [
        'Choose your preferred closing date',
        'We cover all closing costs',
        'No repairs or cleaning required',
      ],
      image: 'https://images.unsplash.com/photo-1570129477492-45c003edd2be?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
    },
  ];

  const benefits = [
    {
      icon: Clock,
      title: 'Fast Closing',
      description: 'Close in as little as 7 days or on your timeline.',
    },
    {
      icon: DollarSign,
      title: 'No Fees',
      description: 'Zero commissions, no hidden costs, no surprises.',
    },
    {
      icon: ShieldCheck,
      title: 'As-Is Purchase',
      description: 'No repairs, no cleaning, no inspections needed.',
    },
    {
      icon: FileText,
      title: 'Simple Process',
      description: 'We handle all paperwork and closing logistics.',
    },
  ];

  // Horizontal step animation variants
  const stepVariants = {
    hidden: (index) => ({
      opacity: 0,
      x: index % 2 === 0 ? -100 : 100,
      scale: 0.9,
    }),
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <div data-testid="how-it-works-page" className="pt-20" ref={containerRef}>
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-white via-emerald/5 to-white relative overflow-hidden">
        {/* Floating background shapes */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div 
            className="absolute top-20 right-20 w-72 h-72 bg-emerald/10 rounded-full blur-3xl"
            animate={{ 
              y: [0, -30, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="absolute bottom-20 left-20 w-72 h-72 bg-gold/10 rounded-full blur-3xl"
            animate={{ 
              y: [0, 30, 0],
              scale: [1, 0.9, 1],
            }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
          <AnimatedSection>
            <span className="inline-block bg-emerald/10 text-emerald font-subheading font-medium text-sm px-4 py-2 rounded-full mb-6">
              Our Process
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-darkgreen mb-6">
              How It Works
            </h1>
            <p className="font-subheading text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Selling your home to Green Palm Consulting is simple, fast, and stress-free. 
              Here's our straightforward 3-step process.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Horizontal Steps Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white via-gray-50/50 to-white relative overflow-hidden">
        {/* Background motion elements */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <motion.div 
            className="absolute top-1/4 left-0 w-full h-1 bg-gradient-to-r from-transparent via-emerald/20 to-transparent"
            style={{ scaleX: scrollYProgress }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Horizontal Timeline Indicator */}
          <div className="hidden lg:flex items-center justify-center mb-16">
            <div className="flex items-center gap-0">
              {steps.map((step, index) => (
                <div key={index} className="flex items-center">
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.2, duration: 0.5 }}
                    className="w-12 h-12 bg-emerald text-white rounded-full flex items-center justify-center font-heading font-bold text-lg shadow-lg z-10"
                  >
                    {index + 1}
                  </motion.div>
                  {index < steps.length - 1 && (
                    <motion.div
                      initial={{ scaleX: 0 }}
                      whileInView={{ scaleX: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.2 + 0.3, duration: 0.6 }}
                      className="w-32 h-1 bg-gradient-to-r from-emerald to-emerald/50 origin-left"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Steps Cards - Horizontal Sliding */}
          <div className="space-y-20 md:space-y-28">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                custom={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={stepVariants}
                data-testid={`how-it-works-step-${index + 1}`}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                {/* Content */}
                <motion.div 
                  className={index % 2 === 1 ? 'lg:order-2' : ''}
                  whileHover={{ x: index % 2 === 0 ? 10 : -10 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex items-center gap-4 mb-6">
                    <motion.div 
                      className="w-16 h-16 bg-emerald/10 rounded-2xl flex items-center justify-center"
                      whileHover={{ scale: 1.1, backgroundColor: 'rgba(4, 99, 7, 0.2)' }}
                      transition={{ duration: 0.3 }}
                    >
                      <step.icon className="w-8 h-8 text-emerald" />
                    </motion.div>
                    <span className="font-heading text-6xl font-extrabold text-emerald/10">
                      {step.number}
                    </span>
                  </div>
                  
                  <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-darkgreen mb-4">
                    {step.title}
                  </h2>
                  
                  <p className="font-body text-gray-600 text-lg mb-8 leading-relaxed">
                    {step.description}
                  </p>

                  <ul className="space-y-4">
                    {step.details.map((detail, detailIndex) => (
                      <motion.li 
                        key={detailIndex} 
                        className="flex items-start gap-3"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 + detailIndex * 0.1, duration: 0.5 }}
                      >
                        <CheckCircle2 className="w-5 h-5 text-emerald mt-0.5 flex-shrink-0" />
                        <span className="font-body text-gray-700">{detail}</span>
                      </motion.li>
                    ))}
                  </ul>
                </motion.div>

                {/* Image */}
                <motion.div 
                  className={index % 2 === 1 ? 'lg:order-1' : ''}
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.4 }}
                >
                  <div className="relative group">
                    <motion.div 
                      className="absolute -inset-4 bg-gradient-to-br from-emerald/20 to-gold/20 rounded-3xl blur-xl opacity-50"
                      animate={{ 
                        scale: [1, 1.02, 1],
                        opacity: [0.5, 0.7, 0.5],
                      }}
                      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                    />
                    <img
                      src={step.image}
                      alt={step.title}
                      className="relative w-full h-80 md:h-96 object-cover rounded-2xl shadow-xl group-hover:shadow-2xl transition-shadow duration-500"
                    />
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-gray-50 via-emerald/5 to-gray-50 relative overflow-hidden">
        {/* Floating shapes */}
        <div className="absolute inset-0 pointer-events-none">
          <motion.div 
            className="absolute top-10 right-10 w-40 h-40 bg-gold/10 rounded-full blur-2xl"
            animate={{ y: [0, -20, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="absolute bottom-10 left-10 w-60 h-60 bg-emerald/10 rounded-full blur-2xl"
            animate={{ y: [0, 20, 0] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection className="text-center mb-16">
            <span className="inline-block bg-gold/20 text-gold font-subheading font-medium text-sm px-4 py-2 rounded-full mb-4 border border-gold/30">
              Why Choose Us
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-darkgreen mb-4">
              The Green Palm Advantage
            </h2>
            <p className="font-subheading text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Experience the easiest way to sell your home with our transparent, hassle-free process.
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <motion.div 
                  data-testid={`benefit-item-${index}`}
                  className="bg-white rounded-2xl p-8 border border-gray-100 text-center h-full card-premium"
                  whileHover={{ 
                    y: -10,
                    boxShadow: '0 25px 50px rgba(10, 61, 45, 0.15)',
                  }}
                  transition={{ duration: 0.4 }}
                >
                  <motion.div 
                    className="w-14 h-14 bg-emerald/10 rounded-xl flex items-center justify-center mx-auto mb-6"
                    whileHover={{ scale: 1.1, backgroundColor: 'rgba(4, 99, 7, 0.2)' }}
                  >
                    <benefit.icon className="w-7 h-7 text-emerald" />
                  </motion.div>
                  <h3 className="font-heading text-xl font-bold text-darkgreen mb-3">
                    {benefit.title}
                  </h3>
                  <p className="font-body text-gray-600 text-sm leading-relaxed">
                    {benefit.description}
                  </p>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-darkgreen relative overflow-hidden">
        <div className="absolute inset-0">
          <motion.div 
            className="absolute top-0 left-0 w-96 h-96 bg-emerald/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"
            animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.3, 0.2] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="absolute bottom-0 right-0 w-96 h-96 bg-gold/15 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"
            animate={{ scale: [1, 0.9, 1], opacity: [0.15, 0.25, 0.15] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <AnimatedSection>
            <motion.div 
              className="w-20 h-20 bg-emerald rounded-full flex items-center justify-center mx-auto mb-8"
              whileHover={{ scale: 1.1 }}
              animate={{ 
                boxShadow: ['0 0 0 0 rgba(4, 99, 7, 0.4)', '0 0 0 20px rgba(4, 99, 7, 0)', '0 0 0 0 rgba(4, 99, 7, 0)'],
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Phone className="w-10 h-10 text-white" />
            </motion.div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="font-subheading text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto">
              Take the first step toward selling your home. Get your free, no-obligation cash offer today.
            </p>
            <Link
              to="/get-offer"
              data-testid="how-it-works-cta-button"
              className="btn-premium inline-flex items-center justify-center bg-emerald hover:bg-emerald-dark text-white font-bold text-lg px-10 py-5 rounded-full group"
            >
              Get Your Free Offer
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default HowItWorksPage;
